import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to reset password. Token may be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-neo-bg dark:bg-[#141414] flex items-center justify-center px-4">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8 max-w-md">
          <h2 className="font-heading font-bold text-xl mb-2 dark:text-white">Invalid Reset Link</h2>
          <p className="text-sm text-neo-text-secondary dark:text-gray-400 mb-4">This reset link is invalid or has expired.</p>
          <button onClick={() => navigate("/forgot-password")} className="neo-btn neo-btn-primary" data-testid="request-new-reset-btn">
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neo-bg dark:bg-[#141414] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-neo-yellow border-[3px] border-neo-border dark:border-[#444] shadow-[4px_4px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-neo-text" />
          </div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight dark:text-white">New Password</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-2">Choose a strong password for your account</p>
        </div>

        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
          {!success ? (
            <>
              {error && (
                <div className="mb-4 p-3 rounded-lg border-2 border-neo-red bg-neo-red/10 text-sm text-neo-text dark:text-white" data-testid="reset-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      className="pl-10 pr-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neo-text-secondary dark:text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`neo-btn w-full py-3 ${loading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"}`}
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-neo-green border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-neo-text" />
              </div>
              <h2 className="font-heading font-bold text-xl mb-2 dark:text-white">Password Reset!</h2>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400 mb-4">Your password has been updated. You can now sign in.</p>
              <button onClick={() => navigate("/login")} className="neo-btn neo-btn-primary" data-testid="go-to-login-btn">
                Go to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
